/**
 * Info Page
 * Legal and company information hub
 */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { PageWrapper } from '@components/layout/page-wrapper';
import { GlassCard } from '@components/primitives/glass-card';
import { IconWrapper } from '@components/primitives/icon';
import { House, FileText, ShieldCheck, Cookie, Database, EnvelopeSimple, Trash, Eraser } from '@phosphor-icons/react';
import { colors } from '@config/seafoam_colors';
import { spacing, borderRadius } from '@config/spacing';
import { typography } from '@config/typography';
import { media } from '@config/breakpoints';

const InfoContainer = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing['5xl']} ${spacing.xl};
`;

const InfoCard = styled(GlassCard)`
  gap: ${spacing['2xl']};
  max-width: 600px;
  padding: ${spacing['4xl']};
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  text-align: center;
  align-items: center;
`;

const HomeIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: ${colors.brand.primary};
  border-radius: ${borderRadius.full};
  cursor: pointer;
  transition: transform 0.2s ease-out;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Headline = styled.h1`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text};
  margin: 0;

  ${media.md} {
    font-size: ${typography.fontSize['4xl']};
  }
`;

const LinksSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  width: 100%;
`;

const InfoLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.lg};
  background: ${colors.glassSurface};
  border: 1px solid ${colors.glassBorder};
  border-radius: ${borderRadius.lg};
  color: ${colors.text};
  text-decoration: none;
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeights.medium};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${colors.glassSurfaceHover};
    border-color: ${colors.highlight.primary};
    transform: translateX(4px);
  }

  svg {
    flex-shrink: 0;
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: ${spacing.xl};
  border-top: 1px solid ${colors.border};
  color: ${colors.textSecondary};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
`;

/**
 * Info page component
 */
export const InfoPage: React.FC = () => {
  const navigate = useNavigate();

  const handleHomeClick = () => {
    navigate('/');
  };

  return (
    <PageWrapper>
      <InfoContainer>
        <InfoCard>
          <Header>
            <HomeIconWrapper onClick={handleHomeClick}>
              <IconWrapper $size={32} $color={colors.highlight.light}>
                <House weight="fill" />
              </IconWrapper>
            </HomeIconWrapper>
            <Headline>Information</Headline>
          </Header>

          <LinksSection>
            <InfoLink href="/legal/terms-of-service">
              <IconWrapper $size={24} $color={colors.highlight.primary}>
                <FileText weight="regular" />
              </IconWrapper>
              Terms of Service
            </InfoLink>

            <InfoLink href="/legal/privacy-policy">
              <IconWrapper $size={24} $color={colors.highlight.primary}>
                <ShieldCheck weight="regular" />
              </IconWrapper>
              Privacy Policy
            </InfoLink>

            <InfoLink href="/legal/privacy-policy#cookies">
              <IconWrapper $size={24} $color={colors.highlight.primary}>
                <Cookie weight="regular" />
              </IconWrapper>
              Cookie Policy
            </InfoLink>

            <InfoLink href="/legal/data-use">
              <IconWrapper $size={24} $color={colors.highlight.primary}>
                <Database weight="regular" />
              </IconWrapper>
              Data Use
            </InfoLink>

            <InfoLink href="/legal/contact-us">
              <IconWrapper $size={24} $color={colors.highlight.primary}>
                <EnvelopeSimple weight="regular" />
              </IconWrapper>
              Contact Us
            </InfoLink>

            <InfoLink href="/legal/delete-my-data">
              <IconWrapper $size={24} $color={colors.highlight.primary}>
                <Eraser weight="regular" />
              </IconWrapper>
              Request Data Deletion
            </InfoLink>

            <InfoLink href="/legal/delete-data">
              <IconWrapper $size={24} $color={colors.highlight.primary}>
                <Trash weight="regular" />
              </IconWrapper>
              Delete Account
            </InfoLink>
          </LinksSection>

          <Copyright>
            &copy; {new Date().getFullYear()} Lat Long Labs LLC. All rights reserved.
          </Copyright>
        </InfoCard>
      </InfoContainer>
    </PageWrapper>
  );
};
